import LegalPageLayout from '../../components/feature/LegalPageLayout';

const sections = [
  { id: 'introduction', title: 'Introduction' },
  { id: 'information-we-collect', title: 'Information We Collect' },
  { id: 'how-we-use-information', title: 'How We Use Your Information' },
  { id: 'analytics-tracking', title: 'Analytics and Tracking' },
  { id: 'submitted-tool-information', title: 'Submitted Tool Information' },
  { id: 'third-party-services', title: 'Third-Party Services' },
  { id: 'data-security', title: 'Data Security' },
  { id: 'your-rights', title: 'Your Rights' },
  { id: 'children-privacy', title: 'Children\'s Privacy' },
  { id: 'changes-to-policy', title: 'Changes to This Policy' },
  { id: 'contact-us', title: 'Contact Us' },
];

export default function PrivacyPolicyPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://toolnova.ai';
  return (
    <LegalPageLayout
      title="Privacy Policy"
      lastUpdated="January 2025"
      sections={sections}
      seo={{
        title: 'Privacy Policy - How ToolNova Protects Your Data | ToolNova',
        description: 'Read the ToolNova Privacy Policy to understand how we collect, use, and protect your personal information when you use our AI tools directory.',
        keywords: 'toolnova privacy policy, data protection, personal information, AI tools directory privacy',
        canonical: `${siteUrl}/privacy-policy`,
        schema: {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Privacy Policy',
          description: 'ToolNova Privacy Policy — how we collect, use, and protect your personal information.',
          url: `${siteUrl}/privacy-policy`,
          publisher: {
            '@type': 'Organization',
            name: 'ToolNova',
            url: siteUrl,
          },
        },
      }}
    >
      <section id="introduction">
        <h2>Introduction</h2>
        <p>
          Welcome to Toolnova. We are committed to protecting your privacy and ensuring transparency about how we collect, use, and safeguard your personal information. This Privacy Policy explains our practices regarding data collection and usage when you visit our website, submit AI tools to our directory, or interact with our services.
        </p>
        <p>
          By using Toolnova, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
        </p>
      </section>

      <section id="information-we-collect">
        <h2>Information We Collect</h2>
        <p>
          We collect several types of information to provide and improve our services:
        </p>
        
        <h3>Personal Information</h3>
        <p>
          When you submit a tool to our directory or contact us, we may collect:
        </p>
        <ul>
          <li><strong>Contact Information:</strong> Name, email address, and company name</li>
          <li><strong>Tool Information:</strong> Tool name, website URL, description, category, and logo</li>
          <li><strong>Communication Data:</strong> Messages you send through our contact form</li>
        </ul>

        <h3>Automatically Collected Information</h3>
        <p>
          When you visit Toolnova, we automatically collect certain information about your device and browsing behavior:
        </p>
        <ul>
          <li><strong>Usage Data:</strong> Pages visited, time spent on pages, click patterns, and navigation paths</li>
          <li><strong>Device Information:</strong> Browser type, operating system, device type, and screen resolution</li>
          <li><strong>Technical Data:</strong> IP address, referring URLs, and access times</li>
        </ul>
      </section>

      <section id="how-we-use-information">
        <h2>How We Use Your Information</h2>
        <p>
          We use the collected information for various purposes:
        </p>
        <ul>
          <li><strong>Service Delivery:</strong> To review and list submitted AI tools in our directory</li>
          <li><strong>Communication:</strong> To respond to your inquiries and provide customer support</li>
          <li><strong>Improvement:</strong> To analyze usage patterns and improve our website functionality</li>
          <li><strong>Content Curation:</strong> To maintain and update our AI tools directory with relevant information</li>
          <li><strong>Marketing:</strong> To send newsletters and updates about new AI tools (with your consent)</li>
          <li><strong>Security:</strong> To detect, prevent, and address technical issues and fraudulent activity</li>
        </ul>
      </section>

      <section id="analytics-tracking">
        <h2>Analytics and Tracking</h2>
        <p>
          Toolnova uses analytics services to understand how visitors interact with our website. These services may use cookies and similar tracking technologies to collect and analyze information about your use of our services.
        </p>
        
        <h3>What We Track</h3>
        <ul>
          <li>Page views and session duration</li>
          <li>Traffic sources and referral websites</li>
          <li>Search queries within our directory</li>
          <li>Tool clicks and category browsing patterns</li>
          <li>Device and browser information</li>
        </ul>

        <h3>Purpose of Analytics</h3>
        <p>
          We use analytics data to improve user experience, understand which AI tools are most popular, optimize our content strategy, and make data-driven decisions about directory improvements.
        </p>
      </section>

      <section id="submitted-tool-information">
        <h2>Submitted Tool Information</h2>
        <p>
          When you submit an AI tool to Toolnova, we collect and process the information you provide:
        </p>
        
        <h3>Review Process</h3>
        <p>
          All submitted tools undergo a review process where our team evaluates the tool's relevance, functionality, and quality. During this process:
        </p>
        <ul>
          <li>Your contact information is kept confidential and used only for communication about the submission</li>
          <li>Tool information may be edited for clarity and consistency before publication</li>
          <li>We may visit the tool's website to verify functionality and gather additional information</li>
        </ul>

        <h3>Public Display</h3>
        <p>
          Once approved, the following information becomes publicly visible in our directory:
        </p>
        <ul>
          <li>Tool name and logo</li>
          <li>Tool description and category</li>
          <li>Website URL and external links</li>
        </ul>
        <p>
          Your personal contact information (name and email) is never displayed publicly.
        </p>
      </section>

      <section id="third-party-services">
        <h2>Third-Party Services</h2>
        <p>
          Toolnova may contain links to third-party websites and services, including the AI tools listed in our directory. We are not responsible for the privacy practices of these external sites.
        </p>
        
        <h3>Affiliate Relationships</h3>
        <p>
          Some tool links may be affiliate links, meaning we may earn a commission if you visit or purchase through these links. This does not affect the information we collect about you, but the third-party tool provider may collect information according to their own privacy policy.
        </p>

        <h3>External Services We Use</h3>
        <ul>
          <li><strong>Analytics Providers:</strong> To track website usage and performance</li>
          <li><strong>Hosting Services:</strong> To store and deliver website content</li>
          <li><strong>Email Services:</strong> To send communications and newsletters</li>
        </ul>
      </section>

      <section id="data-security">
        <h2>Data Security</h2>
        <p>
          We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
        </p>
        <ul>
          <li>Secure data transmission using encryption (HTTPS)</li>
          <li>Regular security assessments and updates</li>
          <li>Access controls limiting who can view personal information</li>
          <li>Secure storage of submitted tool information</li>
        </ul>
        <p>
          However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
        </p>
      </section>

      <section id="your-rights">
        <h2>Your Rights</h2>
        <p>
          Depending on your location, you may have certain rights regarding your personal information:
        </p>
        <ul>
          <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
          <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
          <li><strong>Deletion:</strong> Request deletion of your personal information</li>
          <li><strong>Objection:</strong> Object to our processing of your personal information</li>
          <li><strong>Portability:</strong> Request transfer of your information to another service</li>
          <li><strong>Withdraw Consent:</strong> Withdraw consent for marketing communications at any time</li>
        </ul>
        <p>
          To exercise any of these rights, please contact us at <strong>toolnova.hq@gmail.com</strong>. We will respond to your request within 30 days.
        </p>
      </section>

      <section id="children-privacy">
        <h2>Children's Privacy</h2>
        <p>
          Toolnova is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately, and we will take steps to remove that information from our systems.
        </p>
      </section>

      <section id="changes-to-policy">
        <h2>Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. When we make changes, we will update the "Last Updated" date at the top of this policy.
        </p>
        <p>
          We encourage you to review this Privacy Policy periodically. Significant changes will be communicated through a notice on our website or via email if you have subscribed to our communications.
        </p>
      </section>

      <section id="contact-us">
        <h2>Contact Us</h2>
        <p>
          If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
        </p>
        <ul>
          <li><strong>Email:</strong> toolnova.hq@gmail.com</li>
          <li><strong>Contact Form:</strong> Available on our Contact page</li>
        </ul>
        <p>
          We are committed to resolving any privacy concerns you may have and will respond to all inquiries within a reasonable timeframe.
        </p>
      </section>
    </LegalPageLayout>
  );
}